import React, { Component } from 'react';
import ApiService from '../services/api';
import { t } from '../i18n';
import HScrollRow, { scrollItemIntoView } from '../common/h-scroll';
import { KB } from '../widget/component-contract';

// auto 档的分界：分类数 ≤ 这个值走 flat（分类条一行放得下、筛选比钻取快），
// 超过就走 nested（分类条再横滚也不好找了，Crisp/Intercom 同样在"分类多"时改用
// 分类列表落地页）。判定发生在 widget 侧、按实际拉到的分类数算 —— 服务端不知道
// 某个 app 有几个分类，也不该为了这个再多查一次。
const AUTO_NESTED_THRESHOLD = KB.nestedThreshold;

export default class KnowledgeList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            articles: [],
            loading: true,
            searchQuery: '',
            categories: [],
            selectedCategory: null,
            searching: false, // 搜索中状态
            // ---- nested 档的两层导航 ----
            // 'categories'（分类列表，知识库 tab 的落地页）| 'articles'（某个分类下的文章列表）
            nestedView: 'categories',
            // 进入分类时记住整条分类记录（返回栏要显示它的名字；"全部文章"入口用 id:null 的伪分类）
            activeCategory: null,
            // 'push'（进分类）| 'pop'（返回）| null（初始/重置，不播动画）
            nestedNav: null,
            // 每次层级跳转 +1：进到 render 的 key 里，保证同一层来回切也一定重播动画
            //（key 不变的话 React 复用节点，CSS animation 不会二次触发）
            navNonce: 0,
            // 全部文章总数（服务端 index 回的 total）—— "全部文章"入口右侧的计数
            totalArticles: null,
            // 分类请求是否已有结论（成功或失败都算）。auto 档在这之前不能定版式，
            // 否则先按 flat 画一帧、分类到了再跳 nested，用户会看到布局闪一下。
            catsLoaded: false,
        };
        // 分类胶囊 DOM（key → element），用于把选中项/聚焦项滚进视野
        this._catEls = {};
    }

    componentDidMount() {
        this.loadCategories();
        this.loadArticles();
    }

    componentWillUnmount() {
        clearTimeout(this.searchTimeout);
    }

    // ---------- 版式档位 ----------
    // props.layoutMode 已由 widget 侧归一成 auto|flat|nested。auto 在这里按实际分类数落地。
    // 分类还没拉到（0 个）时 auto 先按 flat 渲染：flat 在无分类下就是一个纯文章列表，
    // 不会先闪一层空的分类列表再跳走。
    effectiveMode = () => {
        const mode = this.props.layoutMode === 'flat' || this.props.layoutMode === 'nested'
            ? this.props.layoutMode
            : 'auto';
        if (mode !== 'auto') return mode;
        return this.state.categories.length > AUTO_NESTED_THRESHOLD ? 'nested' : 'flat';
    }

    // auto 档还没拿到分类数 → 版式未定。这段时间画的是"搜索框 + 加载中"的中性骨架，
    // 两档共有的部分先立住，等分类回来再一次性落到 flat / nested，不做二次跳变。
    modePending = () => (
        this.props.layoutMode !== 'flat'
        && this.props.layoutMode !== 'nested'
        && !this.state.catsLoaded
    )

    // Help tab 被点 / 从别处切回知识库时复位到落地页（nested 的落地页是分类列表）。
    // Know.resetToList 调用；无论当前在哪一层都执行，不要"已经在 list 就跳过"。
    resetToRoot = () => {
        const atRoot = this.state.nestedView === 'categories'
            && !this.state.selectedCategory
            && !this.state.searchQuery;
        if (atRoot) return;
        clearTimeout(this.searchTimeout);
        this.setState((prev) => ({
            nestedView: 'categories',
            activeCategory: null,
            selectedCategory: null,
            searchQuery: '',
            searching: false,
            nestedNav: null, // 从别处切回来不是"返回"，不播方向动画
            navNonce: prev.navNonce + 1,
            loading: true,
        }), this.loadArticles);
    }

    async loadCategories() {
        try {
            const { appId } = this.props;
            const url = `${ApiService.baseURL}/knowledge/categories?app_id=${appId}`;

            const response = await fetch(url);

            if (!response.ok) {
                console.error('Failed to load categories: HTTP', response.status);
                this.setState({ catsLoaded: true }); // 失败也要给 auto 档一个结论(0 个分类 → flat)
                return;
            }

            const result = await response.json();

            if (result.code === 0) {
                // 分类渲染完再把当前选中项滚进视野：分类多到溢出时，选中的那个
                // 可能一开始就在可视区外，用户会以为“筛选没生效”。
                this.setState({ categories: result.data || [], catsLoaded: true }, this.scrollSelectedIntoView);
            } else {
                console.error('Failed to load categories: code', result.code, result.msg || '');
                this.setState({ catsLoaded: true });
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
            this.setState({ catsLoaded: true });
        }
    }

    async loadArticles() {
        try {
            const { appId } = this.props;
            const { selectedCategory, searchQuery } = this.state;

            let url = `${ApiService.baseURL}/knowledge?app_id=${appId}&status=active`;

            if (selectedCategory) {
                // selectedCategory 现在是分类ID
                url += `&category=${encodeURIComponent(selectedCategory)}`;
            }

            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }

            const response = await fetch(url);

            if (!response.ok) {
                console.error('Failed to load knowledge articles: HTTP', response.status);
                this.setState({ loading: false, searching: false });
                return;
            }

            const result = await response.json();

            if (result.code === 0) {
                this.setState((prev) => ({
                    articles: result.data || [],
                    loading: false,
                    searching: false,
                    // 只有"不带任何筛选"的那一次请求才是全库总数
                    totalArticles: (!selectedCategory && !searchQuery && typeof result.total === 'number')
                        ? result.total
                        : prev.totalArticles,
                }));
            } else {
                console.error('Failed to load knowledge articles: code', result.code, result.msg || '');
                this.setState({ loading: false, searching: false });
            }
        } catch (error) {
            console.error('Failed to load knowledge articles:', error);
            this.setState({ loading: false, searching: false });
        }
    }

    handleSearch = (e) => {
        const searchQuery = e.target.value;
        this.setState({ searchQuery, searching: true }, () => {
            // Debounce search
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.loadArticles();
            }, KB.searchDebounceMs);
        });
    }

    // 选中分类居中滚入视野（进入知识库、或外部改了选中项时）
    scrollSelectedIntoView = () => {
        const key = this.state.selectedCategory == null ? 'all' : String(this.state.selectedCategory);
        const el = this._catEls && this._catEls[key];
        if (el) scrollItemIntoView(el, 'center');
    }

    handleCategoryFilter = (category) => {
        this.setState({
            selectedCategory: category,
            loading: true
        }, () => {
            this.loadArticles();
        });
    }

    // ---------- nested 档：分类列表 → 文章列表 ----------
    // cat 为 null 表示"全部文章"入口（伪分类，selectedCategory 留空 → 不带 category 参数）
    openCategory = (cat) => {
        clearTimeout(this.searchTimeout);
        this.setState((prev) => ({
            nestedView: 'articles',
            activeCategory: cat || { id: null, name: t('know.all_articles') },
            selectedCategory: cat ? cat.id : null,
            searchQuery: '',
            searching: false,
            nestedNav: 'push',
            navNonce: prev.navNonce + 1,
            loading: true,
        }), this.loadArticles);
    }

    backToCategories = () => {
        clearTimeout(this.searchTimeout);
        this.setState((prev) => ({
            nestedView: 'categories',
            activeCategory: null,
            selectedCategory: null,
            searchQuery: '',
            searching: false,
            nestedNav: 'pop',
            navNonce: prev.navNonce + 1,
            loading: true,
        }), this.loadArticles);
    }

    handleArticleClick = (article) => {
        if (this.props.onArticleClick) {
            this.props.onArticleClick(article);
        }
    }

    getDescription = (article) => {
        // 优先使用API返回的description字段
        if (article.description) {
            return article.description;
        }
        // 否则从content中提取
        if (!article.content) return '';
        const plainText = article.content.replace(/<[^>]*>/g, '');
        // 两行摘要（CSS line-clamp 截断），给足字符量避免第一行就断
        return plainText.length > KB.summaryChars
            ? plainText.substring(0, KB.summaryChars) + '...'
            : plainText;
    }

    _reducedMotion = () => {
        try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
    }

    // ---------- 共享样式 ----------
    _styles() {
        // 主色统一走 --lc-main（widget.js 按当前显示主题写在 shadow host 上，
        // 暗色下已是推导后的暗色主色）。此前是 theme.mainColor 字面量兜底，
        // 与 CSS 变量两条路各走各的。
        return {
            mainColor: 'var(--lc-main)',
            container: {
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--lc-surface, #fff)',
                overflow: 'hidden',
            },
            // 搜索框置顶（Crisp 结构）：flex 列布局第一项，列表区 flex:1 吸收剩余高度。
            // 旧版 absolute + bottom:40px 魔数钉在底部，浮在列表上遮住最后几项，
            // 40px 还和 powered-by 高度隐性耦合 —— 布局流化后不需要任何元素知道
            // 别的元素多高。分隔线随位置改为 border-bottom。
            searchBar: {
                padding: 'var(--lc-space-3) var(--lc-space-4)',
                backgroundColor: 'var(--lc-surface, #ffffff)',
                borderBottom: 'var(--lc-hairline)',
                flexShrink: 0,
            },
            searchWrapper: {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
            },
            searchIcon: {
                position: 'absolute',
                left: 'var(--lc-space-3)',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '18px',
                height: '18px',
                color: 'var(--lc-text-3, #999)',
                pointerEvents: 'none',
            },
            searchInput: {
                width: '100%',
                // 右侧 36px 给内嵌 loading 指示器留位
                padding: '10px 36px 10px 42px',
                border: 'var(--lc-hairline)',
                borderRadius: 'var(--lc-radius-sm)',
                fontSize: `${KB.searchFontSizePx}px`,
                outline: 'none',
                transition: 'border-color var(--lc-dur-base) var(--lc-ease-out)',
            },
            listOuter: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            },
            list: {
                flex: 1,
                overflowY: 'auto',
                padding: '0 0 var(--lc-space-3)',
                minHeight: 0, // 确保flex子元素可以正确滚动
            },
            // 设计语言（Crisp 结构）：图标 + 标题 + 两行摘要 + 右侧 chevron，1px 分隔线，
            // 无描边卡片、无逐卡 View 按钮（整行可点）。
            row: {
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--lc-space-3)',
                padding: `${KB.rowPadVPx}px var(--lc-space-4)`,
                borderBottom: 'var(--lc-hairline)',
                cursor: 'pointer',
                transition: 'background var(--lc-dur-fast) var(--lc-ease-out)',
                backgroundColor: 'transparent',
            },
            rowIcon: {
                width: `${KB.iconTrayPx}px`,
                height: `${KB.iconTrayPx}px`,
                borderRadius: 'var(--lc-radius-sm)',
                background: 'var(--lc-surface-2, #f1f5f9)',
                color: 'var(--lc-text-2, #64748b)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            },
            rowBody: {
                flex: 1,
                minWidth: 0, // 允许flex item缩小
            },
            rowTitle: {
                fontSize: `${KB.rowTitleSizePx}px`,
                fontWeight: '600',
                color: 'var(--lc-text, #1e293b)',
                marginBottom: 'var(--lc-space-1)',
                lineHeight: '1.4',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            rowDesc: {
                fontSize: `${KB.rowSummarySizePx}px`,
                color: 'var(--lc-text-2, #64748b)',
                lineHeight: KB.rowSummaryLineHeight,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
            },
            chevron: {
                flexShrink: 0,
                color: 'var(--lc-text-3, #94a3b8)',
                display: 'flex',
                alignItems: 'center',
            },
            loading: {
                padding: 'var(--lc-space-8)',
                textAlign: 'center',
                color: 'var(--lc-text-3, #94a3b8)',
            },
            empty: {
                padding: 'var(--lc-space-8)',
                textAlign: 'center',
                color: 'var(--lc-text-3, #94a3b8)',
                fontSize: `${KB.searchFontSizePx}px`,
            },
        };
    }

    // 顶部搜索条（三档共用）
    renderSearchBar(placeholder) {
        const s = this._styles();
        const { searchQuery, searching } = this.state;
        return (
            <div style={s.searchBar}>
                <div style={s.searchWrapper}>
                    <svg style={s.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={this.handleSearch}
                        style={s.searchInput}
                        onFocus={(e) => e.target.style.borderColor = s.mainColor}
                        onBlur={(e) => e.target.style.borderColor = 'var(--lc-border, #e5e7eb)'}
                    />
                    {searching && (
                        <span style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                 stroke={s.mainColor} strokeOpacity="0.65" strokeWidth="2.5" strokeLinecap="round"
                                 style={{ animation: 'lcSearchSpin 0.9s linear infinite', display: 'block' }}>
                                <path d="M21 12a9 9 0 1 1-6.2-8.56" />
                            </svg>
                            <style>{`@keyframes lcSearchSpin { to { transform: rotate(360deg); } }`}</style>
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // 文章行（flat / nested / 搜索结果共用）
    renderArticleRow(article) {
        const s = this._styles();
        return (
            <div
                key={article.id}
                style={s.row}
                onClick={() => this.handleArticleClick(article)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--lc-surface-2, #f8fafc)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
                <div style={s.rowIcon} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                </div>
                <div style={s.rowBody}>
                    <div style={s.rowTitle}>{article.title}</div>
                    <div style={s.rowDesc}>
                        {this.getDescription(article)}
                    </div>
                </div>
                <div style={s.chevron} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </div>
            </div>
        );
    }

    renderArticleList() {
        const s = this._styles();
        const { articles, loading, searchQuery, selectedCategory } = this.state;
        if (loading) return <div style={s.loading}>{t('common.loading')}</div>;
        if (articles.length === 0) {
            return (
                <div style={s.empty}>
                    {searchQuery || selectedCategory ? t('home.no_articles') : t('know.empty_kb')}
                </div>
            );
        }
        return articles.map((a) => this.renderArticleRow(a));
    }

    // ---------- flat 档（原有实现，逐字保留：分类条横滚/拖拽/渐隐/箭头全在 HScrollRow） ----------
    renderFlat() {
        const s = this._styles();
        const { categories, selectedCategory } = this.state;

        // 分类条外壳：和搜索框一样是顶部固定条，不被压缩（少了 flexShrink:0，
        // 极矮面板下这行会先被挤扁 —— 胶囊自身 flex-shrink:0，压的是行高）。
        const categoryBarWrapperStyle = {
            borderBottom: 'var(--lc-hairline)',
            flexShrink: 0,
        };

        // 真正的滚动层：胶囊 flex-shrink:0 + nowrap，分类一多就顶出面板宽度，
        // 横滑（含拖拽/滚轮/两端渐隐）统一由 HScrollRow 负责。
        const categoryBarStyle = {
            padding: 'var(--lc-space-3) var(--lc-space-4)',
            display: 'flex',
            gap: 'var(--lc-space-2)',
            whiteSpace: 'nowrap',
        };

        const categoryButtonStyle = (isActive) => ({
            padding: `${KB.categoryPillPadVPx}px var(--lc-space-3)`,
            border: '1px solid ' + (isActive ? 'var(--lc-main)' : 'var(--lc-border, #e5e7eb)'),
            // 胶囊就该是 radius-full。16px 只是"恰好等于 radius-lg"的字面量 ——
            // 审计里 iOS 的 999 才是对的（规范 §1.2 冲突裁决第 4 条）。
            borderRadius: 'var(--lc-radius-full)',
            fontSize: `${KB.categoryPillSizePx}px`,
            cursor: 'pointer',
            backgroundColor: isActive ? 'var(--lc-main)' : 'var(--lc-surface, #ffffff)',
            color: isActive ? 'var(--lc-bubble-user-text, #ffffff)' : 'var(--lc-text, #1e293b)',
            transition: 'background var(--lc-dur-base) var(--lc-ease-out), border-color var(--lc-dur-base) var(--lc-ease-out), color var(--lc-dur-base) var(--lc-ease-out)',
            flexShrink: 0,
        });

        return (
            <div style={s.container}>
                {/* Search Bar at Top（Crisp 结构；沉底版会遮挡最后一项）。
                    左侧固定放大镜；loading 指示器内嵌在输入框右侧（相对搜索框
                    容器绝对定位，垂直居中）——旋转动画只转 rotate，位移交给
                    wrapper，避免旧写法 translateY 进 keyframes 导致 icon 上飘。 */}
                {this.renderSearchBar(t('home.search_articles'))}

                {/* Category Filter */}
                {categories.length > 0 && (
                    <HScrollRow
                        className="lc-cat-scroll"
                        style={categoryBarStyle}
                        wrapperStyle={categoryBarWrapperStyle}
                        accent={'var(--lc-main)'}
                    >
                        <button
                            type="button"
                            ref={(el) => { this._catEls.all = el; }}
                            style={categoryButtonStyle(!selectedCategory)}
                            onClick={() => this.handleCategoryFilter(null)}
                            onFocus={(e) => scrollItemIntoView(e.currentTarget, 'nearest')}
                        >
                            {t('know.all_categories')}
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                type="button"
                                ref={(el) => { this._catEls[String(cat.id)] = el; }}
                                style={categoryButtonStyle(selectedCategory === cat.id)}
                                onClick={() => this.handleCategoryFilter(cat.id)}
                                onFocus={(e) => scrollItemIntoView(e.currentTarget, 'nearest')}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </HScrollRow>
                )}

                {/* Articles List */}
                <div style={s.listOuter}>
                    <div style={s.list}>{this.renderArticleList()}</div>
                </div>
            </div>
        );
    }

    // ---------- nested 档 ----------
    // 返回栏：与 KnowledgeDetail 的返回栏同一套排版（10/12 内边距、32px 主色圆钮、
    // 标题居中、右侧等宽占位保证真居中），这样"层级里的返回"在三层之间是同一个东西。
    renderBackBar(title) {
        const s = this._styles();
        return (
            <div style={{
                padding: 'var(--lc-space-2) var(--lc-space-3)',
                borderBottom: 'var(--lc-hairline)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--lc-space-2)',
                flexShrink: 0,
            }}>
                <button
                    style={{
                        width: `${KB.backButtonPx}px`,
                        height: `${KB.backButtonPx}px`,
                        // 幽灵图标钮,与 KnowledgeDetail 的返回钮同款(见那边注释)。
                        borderRadius: 'var(--lc-radius-full)',
                        border: 'var(--lc-hairline)',
                        background: 'transparent',
                        color: 'var(--lc-text-2, #64748b)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        padding: 0,
                        transition: 'background var(--lc-dur-fast) var(--lc-ease-out), color var(--lc-dur-fast) var(--lc-ease-out)',
                    }}
                    onClick={this.backToCategories}
                    aria-label={t('know.back')}
                    title={t('know.back')}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f1f5f9)'; e.currentTarget.style.color = 'var(--lc-text, #1e293b)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--lc-text-2, #64748b)'; }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'center',
                    fontSize: `${KB.rowTitleSizePx}px`,
                    fontWeight: '600',
                    color: 'var(--lc-text, #1e293b)',
                    lineHeight: '1.35',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    userSelect: 'none',
                }} title={title}>{title}</div>
                {/* 与返回按钮等宽的占位，保证标题真正居中 */}
                <div style={{ width: `${KB.backButtonPx}px`, flexShrink: 0 }} aria-hidden="true" />
            </div>
        );
    }

    // 分类行（Crisp 结构 6.53.19）：分类名 + 描述 + "N 篇文章" + 右侧 chevron。
    // knowledge_categories 表没有 icon 列，所以这里不画图标 —— Crisp 的分类行同样
    // 只有文字，图标是文章行才有的。真有 icon 字段时在这里补一个 rowIcon 即可。
    renderCategoryRow(key, name, description, count, onClick) {
        const s = this._styles();
        return (
            <div
                key={key}
                className="lc-cat-row"
                style={s.row}
                onClick={onClick}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--lc-surface-2, #f8fafc)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
                <div style={s.rowBody}>
                    <div style={s.rowTitle}>{name}</div>
                    {description ? <div style={s.rowDesc}>{description}</div> : null}
                    {count !== null && count !== undefined && (
                        <div style={{ fontSize: `${KB.rowSummarySizePx}px`, color: 'var(--lc-text-3, #94a3b8)', marginTop: 'var(--lc-space-1)' }}>
                            {t(count === 1 ? 'know.article_count_one' : 'know.article_count', { count })}
                        </div>
                    )}
                </div>
                <div style={s.chevron} aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </div>
            </div>
        );
    }

    renderNested() {
        const s = this._styles();
        const { categories, nestedView, activeCategory, nestedNav, navNonce, searchQuery, totalArticles } = this.state;
        const reduced = this._reducedMotion();
        // 方向感知过渡：进分类 = push，返回 = pop，复用 widget 全局的 lcViewPushA/PopA。
        // 每次导航 nestedNav 都会变，key 跟着变 → React 重建节点，动画一定重播。
        const anim = (!nestedNav || reduced) ? {} : {
            animation: `${nestedNav === 'pop' ? 'lcViewPopA' : 'lcViewPushA'} var(--lc-dur-base) var(--lc-ease-out)`,
        };

        // ---- 第二层：某个分类下的文章列表 ----
        if (nestedView === 'articles') {
            return (
                <div style={s.container}>
                    <div key={`cat-${navNonce}`} style={{ ...s.container, ...anim }}>
                        {this.renderBackBar((activeCategory && activeCategory.name) || t('know.all_articles'))}
                        {this.renderSearchBar(t('home.search_articles'))}
                        <div style={s.listOuter}>
                            <div style={s.list}>{this.renderArticleList()}</div>
                        </div>
                    </div>
                </div>
            );
        }

        // ---- 第一层：分类列表（知识库 tab 的落地页）----
        // 搜索时整层换成文章结果：这一层的搜索天然跨分类（selectedCategory 为 null →
        // 请求不带 category 参数），命中的文章直接可点进详情，不用先猜它属于哪个分类。
        const searching = !!searchQuery;
        return (
            <div style={s.container}>
                <div key={`root-${navNonce}`} style={{ ...s.container, ...anim }}>
                    {this.renderSearchBar(t('home.search_articles'))}
                    <div style={s.listOuter}>
                        <div style={s.list}>
                            {searching ? this.renderArticleList() : categories.length === 0 ? (
                                <div style={s.empty}>{t('know.empty_kb')}</div>
                            ) : (
                                <React.Fragment>
                                    {categories.map((cat) => this.renderCategoryRow(
                                        cat.id,
                                        cat.name,
                                        cat.description,
                                        typeof cat.article_count === 'number' ? cat.article_count : null,
                                        () => this.openCategory(cat),
                                    ))}
                                    {/* 兜底入口：没归类的文章只能从这里够到 */}
                                    {this.renderCategoryRow(
                                        '__all__',
                                        t('know.all_articles'),
                                        null,
                                        typeof totalArticles === 'number' ? totalArticles : null,
                                        () => this.openCategory(null),
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // auto 档在分类回来之前的中性骨架：只有搜索框（两档都有）+ 加载中。
    renderPending() {
        const s = this._styles();
        return (
            <div style={s.container}>
                {this.renderSearchBar(t('home.search_articles'))}
                <div style={s.listOuter}>
                    <div style={s.list}><div style={s.loading}>{t('common.loading')}</div></div>
                </div>
            </div>
        );
    }

    render() {
        if (this.modePending()) return this.renderPending();
        return this.effectiveMode() === 'nested' ? this.renderNested() : this.renderFlat();
    }
}
